export function messageFromError(e: unknown, fallback: string): string {
  if (e && typeof e === "object" && "message" in e && typeof (e as { message: unknown }).message === "string") {
    return (e as { message: string }).message;
  }
  return fallback;
}

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function formatColumnName(snakeCase: string | undefined): string {
  if (!snakeCase) return "";

  // Handle special cases like "id" or "url"
  if (snakeCase.toLowerCase() === "id") return "ID";
  if (snakeCase.toLowerCase() === "url") return "URL";

  // Split by underscore and capitalize each word
  return snakeCase
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function formatAnyName(text: string): string {
  return text
    .split(/[-_\s]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function singularize(plural: string): string {
  // Handle irregular plurals
  const irregulars: Record<string, string> = {
    children: "child",
    people: "person",
    men: "man",
    women: "woman",
    teeth: "tooth",
    feet: "foot",
    mice: "mouse",
    geese: "goose",
  };

  if (irregulars[plural.toLowerCase()]) {
    return irregulars[plural.toLowerCase()];
  }

  // Handle common plural endings
  if (plural.endsWith("ies")) {
    return plural.slice(0, -3) + "y";
  }
  if (plural.endsWith("es") && /[sxz]$/.test(plural.slice(0, -2))) {
    return plural.slice(0, -2);
  }
  if (plural.endsWith("s") && !plural.endsWith("ss")) {
    return plural.slice(0, -1);
  }

  // If no rules match, return as is
  return plural;
}

export function pluralize(singular: string): string {
  // Handle irregular plurals
  const irregulars: Record<string, string> = {
    child: "children",
    person: "people",
    man: "men",
    woman: "women",
    tooth: "teeth",
    foot: "feet",
    mouse: "mice",
    goose: "geese",
  };

  // Check if the word is already plural
  const isAlreadyPlural =
    Object.values(irregulars).includes(singular.toLowerCase()) ||
    (singular.endsWith("s") && !singular.endsWith("ss"));

  if (isAlreadyPlural) {
    return singular;
  }

  if (irregulars[singular.toLowerCase()]) {
    return irregulars[singular.toLowerCase()];
  }

  // Handle common plural endings
  if (singular.endsWith("y") && !/[aeiou]y$/.test(singular)) {
    return singular.slice(0, -1) + "ies";
  }
  if (singular.endsWith("f")) {
    return singular.slice(0, -1) + "ves";
  }
  if (singular.endsWith("fe")) {
    return singular.slice(0, -2) + "ves";
  }
  if (singular.endsWith("is")) {
    return singular.slice(0, -2) + "es";
  }
  if (singular.endsWith("us")) {
    return singular.slice(0, -2) + "i";
  }
  if (singular.endsWith("um")) {
    return singular.slice(0, -2) + "a";
  }
  if (
    /[sxz]$/.test(singular) ||
    singular.endsWith("ch") ||
    singular.endsWith("sh")
  ) {
    return singular + "es";
  }
  if (singular.endsWith("s") && !singular.endsWith("ss")) {
    return singular + "es";
  }

  // Default case: add "s"
  return singular + "s";
}
